import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { MINUTES_PER_HOUR, hoursToMinutes, minutesToHours } from '../../lib/timesheet-hours';

interface DurationInputProps {
  /** id of the hours field, for the label's htmlFor */
  id: string;
  /** Decimal hours, as the API stores them */
  value: number;
  onChange: (hours: number) => void;
  /** Accessible name of the whole field ("Straordinario"); the two inputs append "ore"/"minuti" */
  label: string;
  maxHours?: number;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Hours and minutes as two small number fields ("8 h 30 min"). Minutes step by 5 on the spinner
 * but take any value typed; the total never goes above maxHours.
 */
export function DurationInput({
  id,
  value,
  onChange,
  label,
  maxHours = 24,
  disabled,
  invalid,
  describedBy,
  className,
}: DurationInputProps) {
  const { t } = useTranslation();
  const totalMinutes = hoursToMinutes(value);
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  const maxMinutes = maxHours * MINUTES_PER_HOUR;

  const emit = (nextHours: number, nextMinutes: number) => {
    const total = clamp(nextHours * MINUTES_PER_HOUR + nextMinutes, 0, maxMinutes);
    onChange(minutesToHours(total));
  };

  const parse = (raw: string) => {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const fieldClass = cn('h-10 w-16 text-center tabular-nums', invalid && 'border-red-500 focus-visible:ring-red-500');

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        max={maxHours}
        step={1}
        value={hours}
        disabled={disabled}
        aria-label={`${label} – ${t('timesheet.duration.hoursAria', 'hours')}`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={fieldClass}
        onChange={(e) => emit(clamp(parse(e.target.value), 0, maxHours), minutes)}
      />
      <span className="text-sm text-muted-foreground">{t('timesheet.duration.hoursUnit', 'h')}</span>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        max={59}
        step={5}
        value={String(minutes).padStart(2, '0')}
        disabled={disabled}
        aria-label={`${label} – ${t('timesheet.duration.minutesAria', 'minutes')}`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={fieldClass}
        onChange={(e) => emit(hours, clamp(parse(e.target.value), 0, 59))}
      />
      <span className="text-sm text-muted-foreground">{t('timesheet.duration.minutesUnit', 'min')}</span>
    </div>
  );
}

export default DurationInput;
